// Projects: the ones you can see (public ones in the organization, private ones you are in) and the tasks given to
// you across all of them. A project opens on its board (project.tsx); a task opens beside it (task.tsx).
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../../lib/api";
import { formatShort, keys, type TeamSummary } from "../../lib/chat";
import {
  PROJECT_STATUS_LABEL, PRIORITY_LABEL, PRIORITY_STYLE, STATUS_DOT, STATUS_LABEL, dayLabel, isOverdue, projectKeys,
  type MyTask, type Project, type ProjectSummary,
} from "../../lib/projects";
import { Alert, Badge, Button, Card, Dialog, Field, Select, Spinner, cx } from "../../components/ui";
import { Icon } from "../../components/icons";
import { atLeast, errorText, useOrg } from "../org";

import { useProjectEvents } from "./events";

export { ProjectPage } from "./project";

type TaskPrefs = { dueEmails: boolean; timezone: string | null };

// The morning email about one's tasks due soon: on unless switched off, and the browser's time zone remembered so it
// arrives in this person's morning.
function useTaskPrefs(slug: string, enabled: boolean) {
  const qc = useQueryClient();
  const prefs = useQuery({ queryKey: ["task-prefs", slug], enabled, queryFn: () => api<TaskPrefs>(`/orgs/${slug}/task-prefs`) });
  const stored = prefs.data?.timezone;
  const loaded = prefs.isSuccess;
  useEffect(() => {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!loaded || !zone || stored === zone) return;
    void api<TaskPrefs>(`/orgs/${slug}/task-prefs`, { method: "PUT", body: { timezone: zone } }).then((p) => qc.setQueryData(["task-prefs", slug], p)).catch(() => undefined);
  }, [loaded, stored, slug, qc]);
  return prefs;
}

export function ProjectsPage() {
  const { org, role } = useOrg();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "tasks" ? "tasks" : "projects";
  const [creating, setCreating] = useState(false);
  useProjectEvents(org.slug);
  const canCreate = atLeast(role, "member");
  const isClient = role === "guest";   // a guest sees projects only as a client
  useTaskPrefs(org.slug, !isClient);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{isClient ? `The work ${org.name} is doing for you: follow it, ask for things, approve it.` : "Plan the work, give it to people, and see where everything stands."}</p>
        </div>
        {canCreate && <Button onClick={() => setCreating(true)}><Icon name="plus" className="h-4 w-4" />New project</Button>}
      </div>
      <div className="mb-5 flex gap-1 border-b border-slate-200 dark:border-slate-800" role="tablist">
        {(isClient ? [["projects", "Your projects"]] as const : [["projects", "All projects"], ["tasks", "My tasks"]] as const).map(([value, label]) => (
          <button
            key={value} type="button" role="tab" aria-selected={tab === value}
            onClick={() => setParams(value === "tasks" ? { tab: "tasks" } : {}, { replace: true })}
            className={cx("-mb-px border-b-2 px-3 py-2 text-sm font-medium", tab === value ? "border-brand-600 text-brand-700 dark:text-brand-100" : "border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100")}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "projects" || isClient ? <ProjectGrid onCreate={canCreate ? () => setCreating(true) : undefined} /> : <MyTasks />}
      {creating && <NewProjectDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

function ProjectGrid({ onCreate }: { onCreate?: () => void }) {
  const { org } = useOrg();
  const [archived, setArchived] = useState(false);
  const list = useQuery({ queryKey: projectKeys.list(org.slug, archived), queryFn: () => api<{ projects: ProjectSummary[] }>(`/orgs/${org.slug}/projects${archived ? "?archived=1" : ""}`) });
  const projects = list.data?.projects ?? [];
  return (
    <>
      {list.isLoading && <div className="flex justify-center p-10 text-brand-600"><Spinner /></div>}
      {list.error && <Alert>{errorText(list.error)}</Alert>}
      {list.data && projects.length === 0 && (
        <Card className="p-10 text-center">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-brand-50 text-brand-600 dark:bg-brand-700/30"><Icon name="board" /></div>
          <p className="font-medium">{archived ? "No archived projects" : "No projects yet"}</p>
          {!archived && <p className="mt-1 text-sm text-slate-500">{onCreate ? "A project holds the tasks for one piece of work: a website, a launch, a client." : "When you're invited to a project, it shows up here."}</p>}
          {!archived && onCreate && <Button className="mt-4" onClick={onCreate}><Icon name="plus" className="h-4 w-4" />New project</Button>}
        </Card>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {projects.map((p) => archived ? <ArchivedCard key={p.id} project={p} /> : <ProjectCard key={p.id} project={p} />)}
      </div>
      <div className="mt-6 text-center">
        <button type="button" onClick={() => setArchived((a) => !a)} className="text-sm text-slate-500 hover:text-slate-800 hover:underline dark:hover:text-slate-200">
          {archived ? "Back to current projects" : "Show archived projects"}
        </button>
      </div>
    </>
  );
}

function ProjectCard({ project: p }: { project: ProjectSummary }) {
  const { org } = useOrg();
  const progress = p.tasks.total ? Math.round((p.tasks.done / p.tasks.total) * 100) : 0;
  return (
    <Link to={`/o/${org.slug}/projects/${p.id}`} className="group flex flex-col rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200 transition hover:ring-brand-300 dark:bg-slate-900 dark:ring-slate-800 dark:hover:ring-brand-700">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-semibold"><span className="min-w-0 break-words group-hover:text-brand-700 dark:group-hover:text-brand-100">{p.name}</span>{p.visibility === "private" && <Icon name="lock" className="h-4 w-4 shrink-0 text-slate-400" />}</p>
          <p className="mt-0.5 font-mono text-xs text-slate-500">{p.key}{p.team ? ` · ${p.team.name}` : ""}{p.clientCount > 0 && p.myRole !== "client" ? " · with client" : ""}</p>
        </div>
        <Badge tone={p.status === "active" ? "brand" : p.status === "on_hold" ? "amber" : "slate"} className="shrink-0">{PROJECT_STATUS_LABEL[p.status]}</Badge>
      </div>
      {p.description && <p className="mt-3 line-clamp-2 text-sm text-slate-600 dark:text-slate-400">{p.description}</p>}
      <div className="mt-auto pt-4">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>{p.tasks.done} of {p.tasks.total} done</span>
          <span>{progress}%</span>
        </div>
        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label={`${p.name}: ${progress}% done`}>
          <div className="h-full rounded-full bg-brand-600" style={{ width: `${progress}%` }} />
        </div>
        <p className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-400">
          <span>{p.tasks.open} open</span>
          {p.tasks.mine > 0 && <span className="font-medium text-brand-700 dark:text-brand-100">{p.tasks.mine} yours</span>}
          {p.tasks.overdue > 0 && <span className="font-medium text-red-600">{p.tasks.overdue} overdue</span>}
          <span>{p.memberCount} {p.memberCount === 1 ? "person" : "people"}</span>
          {p.dueDate && <span>Due {dayLabel(p.dueDate)}</span>}
        </p>
      </div>
    </Link>
  );
}

function ArchivedCard({ project: p }: { project: ProjectSummary }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const canRestore = p.myRole === "admin" || p.myRole === "lead";
  async function restore() {
    setBusy(true); setError(null);
    try {
      await api(`/orgs/${org.slug}/projects/${p.id}/restore`, { method: "POST" });
      await qc.invalidateQueries({ queryKey: projectKeys.all(org.slug) });
      navigate(`/o/${org.slug}/projects/${p.id}`);
    } catch (err) { setError(errorText(err)); setBusy(false); }
  }
  return (
    <Card className="p-5">
      <p className="font-semibold">{p.name}</p>
      <p className="mt-0.5 font-mono text-xs text-slate-500">{p.key} · {p.tasks.total} tasks</p>
      {error && <div className="mt-3"><Alert>{error}</Alert></div>}
      {canRestore && <Button size="sm" variant="secondary" className="mt-4" loading={busy} onClick={restore}>Bring it back</Button>}
    </Card>
  );
}

function MyTasks() {
  const { org } = useOrg();
  const qc = useQueryClient();
  const prefs = useTaskPrefs(org.slug, true);
  async function setEmails(on: boolean) {
    const p = await api<TaskPrefs>(`/orgs/${org.slug}/task-prefs`, { method: "PUT", body: { dueEmails: on } }).catch(() => null);
    if (p) qc.setQueryData(["task-prefs", org.slug], p);
  }
  const emails = prefs.data && (
    <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
      <input type="checkbox" checked={prefs.data.dueEmails} onChange={(e) => void setEmails(e.target.checked)} className="h-4 w-4 rounded" />
      Email me in the morning when something of mine is due tomorrow, or has just gone overdue
    </label>
  );
  const mine = useQuery({ queryKey: projectKeys.mine(org.slug), queryFn: () => api<{ tasks: MyTask[] }>(`/orgs/${org.slug}/my-tasks`) });
  const tasks = mine.data?.tasks ?? [];
  const open = tasks.filter((t) => t.status !== "done");
  const done = tasks.filter((t) => t.status === "done");
  if (mine.isLoading) return <div className="flex justify-center p-10 text-brand-600"><Spinner /></div>;
  if (mine.error) return <Alert>{errorText(mine.error)}</Alert>;
  if (!tasks.length) {
    return (
      <div className="space-y-3">
      {emails}
      <Card className="p-10 text-center">
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30"><Icon name="check" /></div>
        <p className="font-medium">Nothing on your plate</p>
        <p className="mt-1 text-sm text-slate-500">Tasks given to you in any project show up here.</p>
      </Card>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {emails}
      <TaskRows title={`Open · ${open.length}`} tasks={open} empty="All done. Nice." />
      {done.length > 0 && <TaskRows title="Finished this week" tasks={done} />}
    </div>
  );
}

function TaskRows({ title, tasks, empty }: { title: string; tasks: MyTask[]; empty?: string }) {
  const { org } = useOrg();
  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
      <Card className="p-0">
        {!tasks.length && empty && <p className="px-5 py-6 text-sm text-slate-500">{empty}</p>}
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {tasks.map((t) => (
            <li key={t.id}>
              <Link to={`/o/${org.slug}/projects/${t.project.id}?task=${t.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/60">
                <span className={cx("h-2.5 w-2.5 shrink-0 rounded-full", STATUS_DOT[t.status])} title={STATUS_LABEL[t.status]} />
                <span className="font-mono text-xs text-slate-500">{t.ref}</span>
                <span className={cx("min-w-0 flex-1 break-words text-sm font-medium", t.status === "done" && "text-slate-500 line-through")}>{t.title}</span>
                <span className="flex w-full items-center gap-3 pl-5 text-xs text-slate-500 sm:w-auto sm:pl-0">
                  <span className="truncate">{t.project.name}</span>
                  {t.priority !== "none" && <span className={PRIORITY_STYLE[t.priority]}>{PRIORITY_LABEL[t.priority]}</span>}
                  {t.dueDate && <span className={cx(isOverdue(t) && "font-medium text-red-600")}>{isOverdue(t) ? "Overdue · " : "Due "}{dayLabel(t.dueDate)}</span>}
                  {t.status === "done" && t.completedAt && <span>Done {formatShort(t.completedAt)}</span>}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}

function NewProjectDialog({ onClose }: { onClose: () => void }) {
  const { org } = useOrg();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const teams = useQuery({ queryKey: keys.teams(org.slug), queryFn: () => api<{ teams: TeamSummary[] }>(`/orgs/${org.slug}/teams`) });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [visibility, setVisibility] = useState<"public" | "private">("public");

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    const text = (k: string) => String(d.get(k) ?? "").trim();
    setBusy(true); setError(null); setFields({});
    try {
      const r = await api<{ project: Project }>(`/orgs/${org.slug}/projects`, {
        method: "POST",
        body: {
          name: text("name"), description: text("description"), visibility,
          ...(text("key") ? { key: text("key") } : {}),
          teamId: text("teamId") || null, startDate: text("startDate") || null, dueDate: text("dueDate") || null,
        },
      });
      await qc.invalidateQueries({ queryKey: projectKeys.all(org.slug) });
      navigate(`/o/${org.slug}/projects/${r.project.id}`);
    } catch (err) {
      setError(errorText(err));
      if (err instanceof ApiError) setFields(err.fields);
      setBusy(false);
    }
  }

  const myTeams = (teams.data?.teams ?? []).filter((t) => t.myRole);
  return (
    <Dialog open onClose={onClose} title="New project">
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && <Alert>{error}</Alert>}
        <Field label="Name" name="name" required maxLength={120} autoFocus placeholder="Sunrise Bakery website" error={fields.name} />
        <Field label="Short code (optional)" name="key" maxLength={10} placeholder="Made from the name, e.g. SBW" hint="Starts every task's number: SBW-1, SBW-2…" error={fields.key} />
        <div>
          <label htmlFor="project-description" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Description (optional)</label>
          <textarea id="project-description" name="description" rows={3} maxLength={5000} className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
        </div>
        <fieldset>
          <legend className="mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">Who can see it</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {([["public", `Everyone in ${org.name}`, "Anyone can find it and work on it."], ["private", "Only people you add", "Admins can always see it."]] as const).map(([value, label, hint]) => (
              <label key={value} className={cx("flex cursor-pointer gap-3 rounded-lg p-3 ring-1", visibility === value ? "bg-brand-50 ring-brand-300 dark:bg-brand-700/20 dark:ring-brand-700" : "ring-slate-200 dark:ring-slate-700")}>
                <input type="radio" name="visibility" value={value} checked={visibility === value} onChange={() => setVisibility(value)} className="mt-1" />
                <span><span className="block text-sm font-medium">{label}</span><span className="block text-xs text-slate-500">{hint}</span></span>
              </label>
            ))}
          </div>
        </fieldset>
        {myTeams.length > 0 && (
          <Select label="Discussed in (optional)" name="teamId" defaultValue="">
            <option value="">No team</option>
            {myTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Select>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Starts (optional)" name="startDate" type="date" error={fields.startDate} />
          <Field label="Due (optional)" name="dueDate" type="date" error={fields.dueDate} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={busy}>Create project</Button>
        </div>
      </form>
    </Dialog>
  );
}
